import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import './Modal.css';

function Modal({ isOpen, onClose, title, children, footer, size = 'md' }) {
  const panelRef = useRef(null);
  const reduceMotion = useReducedMotion();

  // ⚠️ `onClose` is held in a ref and kept OUT of the effect's dependencies.
  //
  // Nearly every caller passes an inline arrow (`onClose={() => setOpen(false)}`),
  // which is a new function identity on every parent render. With `onClose` in
  // the deps, typing one character into any field inside a modal re-rendered
  // the parent, changed that identity, and made this effect tear down and set
  // up again — the cleanup restoring focus to whatever was focused before the
  // modal opened, and the setup moving it to the panel. Focus left the input on
  // every keystroke, so you could type one letter and then had to click back in.
  //
  // The effect is about the modal being OPEN, not about which callback it was
  // handed, so `isOpen` is the only thing it should react to.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    panelRef.current?.focus();
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      previouslyFocused?.focus?.();
    };
  }, [isOpen]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="ds-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.15 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            ref={panelRef}
            className={`ds-modal-panel ds-modal-${size}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? 'ds-modal-title' : undefined}
            tabIndex={-1}
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 12 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            {title && (
              <div className="ds-modal-header">
                <h2 id="ds-modal-title" className="ds-modal-title">{title}</h2>
                <button className="ds-modal-close" onClick={onClose} aria-label="Close dialog">
                  ✕
                </button>
              </div>
            )}
            <div className="ds-modal-body">{children}</div>
            {footer && <div className="ds-modal-footer">{footer}</div>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export default Modal;
